/**
 * iMessage-style tail on the lower-left (incoming) or lower-right (outgoing).
 *
 * @param {Object} props
 * @param {boolean} props.outgoing When true, the tail sits on the lower right.
 * @returns {JSX.Element}
 */
const BubbleTail = ({ outgoing }) => (
  <svg
    className={`pointer-events-none absolute bottom-0 h-[18px] w-[10px] ${
      outgoing ? "-right-[7px] -scale-x-100 text-[#34C759]" : "-left-[7px] text-[#e9e9eb]"
    }`}
    viewBox="0 0 12 18"
    aria-hidden
  >
    <path fill="currentColor" d="M12 0C8 10 4 15 0 18h12V0Z" />
  </svg>
);

/**
 * SMS-style speech-bubble preview for a single message.
 *
 * @param {Object} props
 * @param {string} props.text - Message body shown in the bubble.
 * @param {string} [props.label] - Caption above the bubble (e.g. "Preview for Aaron").
 * @param {string} [props.helper] - Muted note below the bubble.
 * @param {"incoming"|"outgoing"} [props.variant="incoming"] - Incoming gray or outgoing green.
 * @param {string} [props.className=""] - Extra classes on the wrapper.
 * @returns {JSX.Element|null}
 */
const TextMessagePreview = ({
  text,
  label,
  helper,
  variant = "incoming",
  className = "",
}) => {
  if (!text) {
    return null;
  }

  const isOutgoing = variant === "outgoing";
  const bubbleClass = isOutgoing
    ? "relative max-w-[85%] whitespace-pre-wrap rounded-[18px] rounded-br-sm bg-[#34C759] px-3.5 py-2 text-[15px] leading-snug text-white"
    : "relative max-w-[85%] whitespace-pre-wrap rounded-[18px] rounded-bl-sm bg-[#e9e9eb] px-3.5 py-2 text-[15px] leading-snug text-gray-900";

  return (
    <div className={className}>
      {label ? (
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {label}
        </p>
      ) : null}
      <div className={`flex ${isOutgoing ? "justify-end pr-1.5" : "justify-start pl-1.5"}`}>
        <div className={bubbleClass}>
          {text}
          <BubbleTail outgoing={isOutgoing} />
        </div>
      </div>
      {helper ? <p className="mt-1 text-xs text-gray-400">{helper}</p> : null}
    </div>
  );
};

export default TextMessagePreview;
